/**
 * EditDetailsModal (metaManage) — 460px. Name, product type, change delivery,
 * description. Basic details only (mapping edits go via Builder).
 */
import React from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';
import { Close } from '../icons';
import { Button, Modal, Select } from '../components';
import { useBridge } from '../store/bridgeStore';
import { allInts } from '../domain/catalog';
import { CATEGORIES } from '../data/seeds';

export default function EditDetailsModal() {
  const { state, actions } = useBridge();
  const m = state.metaManage;
  if (!m) return null;
  const a = allInts(state.adapters, state.metaOverrides).find(i => i.id === m.id) || ({} as any);
  const catOptions = CATEGORIES.map(c => ({ value: c, label: c }));

  return (
    <Modal width={460} zIndex={70} onClose={actions.closeMetaManage}>
      <View style={styles.header}>
        <View style={[styles.avatar, { backgroundColor: a.color || '#635bff' } as any]}><Text style={styles.avatarText}>{a.initials}</Text></View>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Edit connector details</Text>
          <Text className="t-mono" style={styles.sub}>{(a.cls || 'custom').toUpperCase()} adapter</Text>
        </View>
        <Pressable onPress={actions.closeMetaManage}><Close size={18} color={colors.ink3} /></Pressable>
      </View>

      <View style={styles.body}>
        <View style={styles.infoBox}>
          <Text style={styles.infoText}>Basic details only. To change how fields translate, use <Text style={{ fontWeight: fontWeight.semibold } as any}>Edit mapping in Builder</Text>.</Text>
        </View>

        <Text style={styles.label}>Display name</Text>
        <TextInput value={m.name} onChangeText={v => actions.metaManageSet({ name: v })} style={styles.input} />

        <View style={styles.split}>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>Product type</Text>
            <Select value={m.category} options={catOptions} onChange={v => actions.metaManageSet({ category: v })} variant="field" height={38} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>Change delivery</Text>
            <View style={styles.cdRow}>
              <Pressable onPress={() => actions.metaManageSet({ change: 'webhook' })} style={[styles.cdBtn, { borderColor: m.change === 'webhook' ? colors.accent : colors.line } as any]}><Text style={styles.cdText}>Webhook</Text></Pressable>
              <Pressable onPress={() => actions.metaManageSet({ change: 'poll' })} style={[styles.cdBtn, { borderColor: m.change === 'poll' ? colors.accent : colors.line } as any]}><Text style={styles.cdText}>Poll</Text></Pressable>
            </View>
          </View>
        </View>

        <Text style={[styles.label, { marginTop: 16 }]}>Description</Text>
        <TextInput value={m.purpose} onChangeText={v => actions.metaManageSet({ purpose: v })} multiline numberOfLines={2} style={[styles.input, { height: 56, paddingTop: 9 }]} />
      </View>

      <View style={styles.footer}>
        <Button label="Cancel" variant="ghost" onPress={actions.closeMetaManage} />
        <Button label="Save changes" onPress={actions.saveMetaManage} />
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  header: { padding: 18, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 12 },
  avatar: { width: 38, height: 38, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, color: '#fff' },
  title: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink },
  sub: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3 },
  body: { padding: 20 },
  infoBox: { padding: 11, paddingHorizontal: 13, borderWidth: 1, borderColor: colors.infoLine, backgroundColor: colors.infoSoft, borderRadius: radii.r3, marginBottom: 18 },
  infoText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.info, lineHeight: 18 },
  label: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, marginBottom: 6 },
  input: { width: '100%', height: 38, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: radii.r2, fontFamily: fontFamily.sans, fontSize: fontSize.md, color: colors.ink as any, outlineStyle: 'none' } as any,
  split: { flexDirection: 'row', gap: 16, marginTop: 16 },
  cdRow: { flexDirection: 'row', gap: 8 },
  cdBtn: { flex: 1, paddingVertical: 9, borderRadius: radii.r2, borderWidth: 1.5, backgroundColor: colors.paper2, alignItems: 'center', cursor: 'pointer' } as any,
  cdText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink },
  footer: { padding: 14, paddingHorizontal: 20, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.paper2, flexDirection: 'row', justifyContent: 'flex-end', gap: 10 },
});
